import React, { PureComponent, PropTypes } from 'react';
import { commonPropTypes, eventTypes } from '../../util/constants';
import Separated from '../../common/components/Separated';
import cx from 'classnames';
import EventTeamName from './EventTeamName';
import _ from 'underscore';
import { extractRecruits } from '../../util/EventUtil';

const AWAY_TEAM_INDICATOR = <strong> @ </strong>;
const HOME = 'HOME';
const AWAY = 'AWAY';

const GameSide = ({ schools, separator = ', ', wrap = false, ...rest }) => {
  const schoolClasses = cx({ 'text-nowrap': !wrap });

  return (
    <Separated {...rest} separator={separator}>
      {
        _.map(schools, ({ side, school: { id, name } }) =>
          (
            <span className={schoolClasses} key={`${side}-${id}`}>
              { name }
            </span>
          )
        )
      }
    </Separated>
  );
};

class SchoolGameName extends PureComponent {
  static propTypes = {
    name: PropTypes.string,
    schools: PropTypes.arrayOf(PropTypes.shape({
      side: PropTypes.oneOf([ AWAY, HOME ]).isRequired,
      school: PropTypes.object.isRequired
    })).isRequired,
    wrap: PropTypes.bool,
    schoolId: PropTypes.string
  };

  static defaultProps = {
    name: null,
    wrap: false,
    schoolId: null
  };

  render() {
    const { schools, wrap, name, schoolId, ...rest } = this.props;

    const boldedName = <strong> {name} </strong>;

    let content = null;
    // if we are rendering it from the perspective of a particular school
    if (schoolId) {
      const isHomeGame = _.any(schools, ({ school, side }) => side === HOME && schoolId === school.id);
      const otherSchools = _.filter(schools, ({ school }) => school.id !== schoolId);

      content = (
        <span>
          {
            otherSchools && otherSchools.length > 0 ? (
                <span>
                {isHomeGame ? 'vs. ' : AWAY_TEAM_INDICATOR}
                  <GameSide schools={otherSchools} wrap={wrap}/>
              </span>
              ) : null
          }
          {boldedName}
        </span>
      );
    } else {
      const bySide = _.groupBy(schools, 'side');
      const homeSchools = bySide[ HOME ];
      const awaySchools = bySide[ AWAY ];

      content = (
        <span>
          {
            awaySchools ?
              <GameSide schools={awaySchools} wrap={wrap}/>
              : 'TBA'
          }
          {AWAY_TEAM_INDICATOR}
          {
            homeSchools ?
              <GameSide schools={homeSchools} wrap={wrap}/>
              : 'TBA'
          }
          {boldedName}
        </span>
      );
    }

    return <span {...rest}>{content}</span>;
  }
}

class SchoolTourneyGameName extends PureComponent {
  static propTypes = {
    event: commonPropTypes.EVENT_SHAPE.isRequired
  };

  render() {
    const { className, event } = this.props;
    return <em className={cx('color-rich-orange', className)}>{event.name || 'n/a'}</em>;
  }
}

class TourneyGameName extends PureComponent {
  static propTypes = {
    event: commonPropTypes.EVENT_SHAPE.isRequired,
    teamNameComponent: PropTypes.func
  };

  static defaultProps = {
    teamNameComponent: EventTeamName
  };

  render() {
    const { className, event: { sides } } = this.props;
    const { teamNameComponent: TeamNameComponent, ...rest } = this.props;
    const { home, visitor } = sides;

    return (
      <span className={className}>
          <Separated separator={<strong className="mg-left-sm mg-right-sm">vs.</strong>}>
            { home && home.team ? <TeamNameComponent team={home.team} { ...rest }/> : 'TBD' }
            { visitor && visitor.team ? <TeamNameComponent team={visitor.team} { ...rest }/> : 'TBD' }
          </Separated>
        </span>
    );
  }
}

class RecruitTourneyGameName extends PureComponent {
  static propTypes = {
    event: commonPropTypes.EVENT_SHAPE.isRequired,
    teamNameComponent: PropTypes.func,
    recruit: PropTypes.object
  };

  static defaultProps = {
    teamNameComponent: EventTeamName
  };

  isPotentialGame = () => {
    const { event, recruit } = this.props;
    const eventRecruits = extractRecruits(event);
    return !_.any(eventRecruits, eventRecruit => eventRecruit.id === recruit.id);
  };

  isRecruitHome = () => {
    const { event: { sides }, recruit } = this.props;
    const { home } = sides;

    return _.chain(recruit.recruitTourneys)
      .pluck('teams')
      .flatten(true)
      .pluck('id')
      .any(id => id === home.team.id)
      .value();
  };

  opponentTeamName = () => {
    const { event: { sides } } = this.props;
    const { home, visitor } = sides;

    if (home.team) {
      if (this.isRecruitHome()) {
        return visitor.team;
      } else {
        return home.team;
      }
    } else {
      return null;
    }
  };

  render() {
    const { className, event, teamNameComponent: TeamNameComponent, ...rest } = this.props;
    const team = this.opponentTeamName();

    return (this.isPotentialGame() ?
        <span className={className}> Potential Game: {event.bracketLabel || event.name} </span> :
        <span className={className}>
        <strong>{this.isRecruitHome() ? 'vs. ' : '@ '}</strong>
          { team ? <TeamNameComponent team={team} { ...rest }/> : 'TBD' }
      </span>
    );
  }
}

export default class EventName extends PureComponent {
  static propTypes = {
    event: commonPropTypes.EVENT_SHAPE.isRequired,
    wrap: PropTypes.bool,
    recruit: PropTypes.object,
  };

  static defaultProps = {
    wrap: true
  };

  render() {
    const { className, event, wrap, recruit, schoolId, ...rest } = this.props;

    switch (event.eventType) {
      case eventTypes.GAME:
        if (recruit) {
          return (
            <RecruitTourneyGameName className={className} event={event} recruit={recruit} { ...rest }/>
          );
        } else {
          return <TourneyGameName className={className} event={event} { ...rest }/>;
        }

      case eventTypes.SCHOOL_GAME:
        if (event.tourney) {
          return <SchoolTourneyGameName className={className} event={event}/>;
        } else {
          return (
            <SchoolGameName
              className={className}
              schoolId={schoolId}
              schools={event.schools}
              wrap={wrap}
              name={event.name}
              {...rest}
            />
          );
        }

      default:
        return <span className={className}>{event.name}</span>;
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/components/event/EventName.jsx